import React from 'react';
import { getOutcomeProgressColor } from 'utils/ostUtils';

type ProgressBarProps = {
    /** 0-100; caller is responsible for clamping/deriving this from its own source of truth. */
    percent: number;
    /** When provided, an invisible native range input is overlaid on the bar itself, letting the
     * user drag its handle directly along the fill to set a new percent — the bar is read-only
     * (no slider rendered) when omitted. */
    onChange?: (percent: number) => void;
};

/**
 * Horizontal progress bar filled with a single solid color derived from `percent` (orange at the
 * low end, through amber, to green as it approaches 100) — the whole fill is one flat color per
 * reading, rather than a gradient spanning the bar's length. When `onChange` is passed, the bar
 * doubles as the slider control itself: a native `<input type="range">` is stretched over the
 * track (opacity 0 track/thumb-body, so only our custom-styled thumb shows) so dragging the
 * handle along the bar updates `percent` — and therefore the fill color — live.
 */
export default function ProgressBar({ percent, onChange }: ProgressBarProps) {
    const clamped = Math.max(0, Math.min(100, percent));
    const color = getOutcomeProgressColor(clamped);

    return (
        <div className='progress-bar'>
            <div className='progress-bar__track'>
                <div className='progress-bar__fill-clip'>
                    <div className='progress-bar__fill' style={{ width: `${clamped}%`, background: color }} />
                </div>
                {onChange && (
                    <input
                        type='range'
                        min={0}
                        max={100}
                        value={clamped}
                        onChange={(e) => onChange(Number(e.target.value))}
                        className='progress-bar__slider'
                        aria-label='Progress'
                    />
                )}
            </div>
        </div>
    );
}
